import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PHASES_OF_PLAY, PHASE_OF_PLAY_META } from '@4cm/core/phaseOfPlay';
import type { PhaseCoverage, PhaseOfPlay } from '@4cm/core/phaseOfPlay';
import { Button, Calibrated, Card, GlassPill, SpeakButton } from '../components/primitives';
import { DrillMiniPreview } from '../components/drillCanvas/DrillMiniPreview';
import { synthesizeDrillSpec } from '../components/drillCanvas/synthesizeDrillSpec';
import { DrillPickerSheet } from '../components/DrillPickerSheet';
import { SessionPlayerNoteShare } from '../components/SessionPlayerNoteShare';
import { ReviewFeedbackCard } from '../components/ReviewFeedbackCard';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useSessionDrills } from '../lib/queries/useSessionDrills';
import { loadLocalSessions } from '../lib/localTeam';
import {
  draftSessionNarrative,
  suggestNextSessionTweaks,
  loadSessionReview,
  saveSessionReview,
} from '../lib/sessionReview';
import type { SessionReviewRow } from '../lib/sessionReview';
import './page-shell.css';
import './SessionDetail.css';

interface SessionRow {
  id: string;
  date: string;
  title: string | null;
  duration_mins: number | null;
  objectives: string[] | null;
  psych_social_intent: string | null;
  phase_coverage: Partial<PhaseCoverage> | null;
}

function totalCoverage(coverage: Partial<PhaseCoverage> | null): number {
  if (!coverage) return 0;
  return PHASES_OF_PLAY.reduce((sum, p) => sum + Number(coverage[p] ?? 0), 0);
}

export function SessionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  // Note: post-session review used to gate on level !== 'novice' from
  // useCalibration. The <Calibrated min="GRASSROOTS"> wrapper now does
  // the gating, so neither the hook nor the destructure is needed here.
  const [session, setSession] = useState<SessionRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { drills } = useSessionDrills(id);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [review, setReview] = useState<SessionReviewRow | null>(null);
  const [notesWorking, setNotesWorking] = useState('');
  const [notesIssues, setNotesIssues] = useState('');
  const [nextTweaks, setNextTweaks] = useState('');
  const [aiNarrative, setAiNarrative] = useState<string | null>(null);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewMessage, setReviewMessage] = useState<string | null>(null);
  const [narrativeDrafting, setNarrativeDrafting] = useState(false);
  const [tweaksDrafting, setTweaksDrafting] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      let dbError: string | null = null;
      try {
        const { data, error } = await supabase
          .from('sessions')
          .select('id, date, title, duration_mins, objectives, psych_social_intent, phase_coverage')
          .eq('id', id)
          .maybeSingle();
        if (cancelled) return;
        if (error) {
          dbError = error.message;
        } else if (data) {
          setSession(data as SessionRow);
          setError(null);
          return;
        }
      } catch (err) {
        dbError = err instanceof Error ? err.message : String(err);
      }
      if (cancelled) return;
      const local = loadLocalSessions().find((s) => s.id === id);
      if (local) {
        setSession({
          id: local.id,
          date: local.date,
          title: local.title,
          duration_mins: local.duration_mins,
          objectives: local.objectives,
          psych_social_intent: local.psych_social_intent,
          phase_coverage: local.phase_coverage,
        });
        setError(null);
        return;
      }
      setSession(null);
      if (dbError) setError(dbError);
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      const row = await loadSessionReview(id);
      if (cancelled) return;
      setReview(row);
      setNotesWorking(row?.notes_working ?? '');
      setNotesIssues(row?.notes_issues ?? '');
      setNextTweaks(row?.next_session_tweaks ?? '');
      setAiNarrative(row?.ai_narrative ?? null);
    })();
    return () => { cancelled = true; };
  }, [id]);

  const persistReview = useCallback(
    async (status: 'draft' | 'complete') => {
      if (!id || !user) return;
      // Anti-spam: hide-rather-than-disable means we still get rapid clicks
      // through the keyboard. Bail if a save is already in flight.
      if (reviewSaving) return;
      setReviewSaving(true);
      setReviewMessage(null);
      const result = await saveSessionReview({
        sessionId: id,
        coachId: user.id,
        notesWorking,
        notesIssues,
        nextSessionTweaks: nextTweaks,
        aiNarrative,
        status,
      });
      setReviewSaving(false);
      if (result.ok) {
        setReview(result.review);
        const where = result.source === 'local' ? ' locally' : '';
        setReviewMessage(status === 'complete' ? `Marked complete${where}.` : `Saved${where}.`);
      } else {
        setReviewMessage(result.error);
      }
    },
    [id, user, notesWorking, notesIssues, nextTweaks, aiNarrative, reviewSaving],
  );

  const draftNarrative = useCallback(async () => {
    if (!session) return;
    if (narrativeDrafting) return; // anti-spam, see persistReview comment
    setNarrativeDrafting(true);
    setReviewMessage(null);
    // The deps array below intentionally includes narrativeDrafting so the
    // captured value is fresh when the guard checks it.
    try {
      const text = await draftSessionNarrative({
        sessionTitle: session.title,
        sessionDate: session.date,
        durationMins: session.duration_mins,
        objectives: session.objectives ?? [],
        notesWorking,
        notesIssues,
        nextSessionTweaks: nextTweaks,
      });
      if (!text) {
        setReviewMessage('Brain unreachable — write your own narrative below.');
      } else {
        setAiNarrative(text);
      }
    } catch (err) {
      setReviewMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setNarrativeDrafting(false);
    }
  }, [session, notesWorking, notesIssues, nextTweaks, narrativeDrafting]);

  const suggestTweaks = useCallback(async () => {
    if (!session || tweaksDrafting) return;
    setTweaksDrafting(true);
    setReviewMessage(null);
    try {
      const text = await suggestNextSessionTweaks({
        sessionTitle: session.title,
        objectives: session.objectives ?? [],
        notesWorking,
        notesIssues,
      });
      if (!text) setReviewMessage('Brain unreachable — write your own tweaks below.');
      else setNextTweaks((prev) => (prev.trim() ? `${prev.trim()}\n${text}` : text));
    } catch (err) {
      setReviewMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setTweaksDrafting(false);
    }
  }, [session, notesWorking, notesIssues, tweaksDrafting]);

  const coverageTotal = totalCoverage(session?.phase_coverage ?? null);
  const reviewDirty =
    notesWorking !== (review?.notes_working ?? '')
    || notesIssues !== (review?.notes_issues ?? '')
    || nextTweaks !== (review?.next_session_tweaks ?? '')
    || aiNarrative !== (review?.ai_narrative ?? null);

  return (
    <main className="main page session-detail-page">
      <header className="page-head session-detail-head">
        <Link to="/sessions" className="page-back">← Sessions</Link>
        <h1 className="page-title">{session?.title ?? 'Session'}</h1>
        {session?.date && (
          <p className="page-sub">{new Date(session.date).toLocaleString()}</p>
        )}
      </header>

      {id && (
        <div className="session-detail__run-cta">
          {/* Spec §03 wireframes: Run on field is the primary CTA for novice;
              still present (alongside other actions) for academy. */}
          <Link to={`/sessions/${id}/run`} className="session-detail__run-link">
            Run on field →
          </Link>
          {/* 2D ↔ 3D tactics toggle. Per Phase B + scope doc decision 6:
              novice → 2D default, academy → 3D default. Both reachable. */}
          <Link to={`/sessions/${id}/2d`} className="session-detail__board-link">
            2D board
          </Link>
          <Link to={`/sessions/${id}/3d`} className="session-detail__board-link">
            3D board
          </Link>
        </div>
      )}

      {error && <Card variant="opaque"><p className="page-error">{error}</p></Card>}

      {session?.phase_coverage && coverageTotal > 0 && (
        <section className="session-detail__coverage" aria-label="Phase coverage">
          <div className="page-section-label">Phase coverage</div>
          <div className="session-detail__chips">
            {PHASES_OF_PLAY.map((phase) => {
              const value = Number(session.phase_coverage?.[phase] ?? 0);
              if (value <= 0) return null;
              const pct = Math.round((value / coverageTotal) * 100);
              return (
                <GlassPill key={phase} mono>
                  <span aria-hidden="true">{PHASE_OF_PLAY_META[phase].glyph}</span>
                  {PHASE_OF_PLAY_META[phase].label} {pct}%
                </GlassPill>
              );
            })}
          </div>
        </section>
      )}

      {session?.objectives && session.objectives.length > 0 && (
        <section className="session-detail__objectives" aria-label="Objectives">
          <div className="page-section-label">Objectives</div>
          <ul className="session-detail__list">
            {session.objectives.map((o, i) => <li key={i}>{o}</li>)}
          </ul>
        </section>
      )}

      {session?.psych_social_intent && (
        <Card variant="opaque" className="session-detail__intent">
          <div className="page-section-label">Psych-social intent</div>
          <p>{session.psych_social_intent}</p>
        </Card>
      )}

      {session && (
        <div style={{ display: 'flex', gap: 8, margin: '16px 0', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            style={{
              padding: '8px 14px',
              background: 'var(--brand, #2980B9)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            + Add drill from library
          </button>
          <button
            type="button"
            onClick={() => {
              const obj = session?.objectives?.[0] ? ` about "${session.objectives[0]}"` : '';
              const psy = session?.psych_social_intent ? ` Mental-social intent: ${session.psych_social_intent}` : '';
              window.dispatchEvent(
                new CustomEvent('brain-chat:open', {
                  detail: {
                    seed: `I'm planning the session on ${session?.date ?? 'this date'}${obj}. ${psy} If you find a drill in the library that fits, suggest it. If nothing fits well, propose a new one and we'll add it.`,
                  },
                }),
              );
            }}
            style={{
              padding: '8px 14px',
              background: 'transparent',
              color: 'inherit',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            ✨ Ask Brain for a drill
          </button>
        </div>
      )}
      {drills.length > 0 && (
        <section className="session-detail__drills" aria-label="Drills">
          <div className="page-section-label">Drills · {drills.length}</div>
          <ul className="session-detail__drill-list">
            {drills.map((row, idx) => {
              const drill = row.drill;
              if (!drill) return null;
              const phase = drill.primary_phase as PhaseOfPlay | null;
              const isCurated = drill.source_type === 'CURATED_EXPERT';
              return (
                <li key={`${drill.id}-${idx}`}>
                  <Link to={`/drills/${drill.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <Card variant="opaque" className="drill-card">
                    <div className="drill-card__pitch">
                      {/* One unified preview everywhere. When a drill has no
                          spec (e.g. Brain-planned session drills) synthesise a
                          sensible layout so it never renders empty. */}
                      <DrillMiniPreview
                        spec={drill.drill_spec ?? synthesizeDrillSpec({
                          drillType: drill.drill_type,
                          primaryPhase: drill.primary_phase,
                          name: drill.name,
                        })}
                        width={280}
                      />
                    </div>
                    <div className="drill-card__body">
                      <h2 className="drill-card__title">{drill.name ?? 'Untitled drill'}</h2>
                      <div className="drill-card__pills">
                        {phase && (
                          <GlassPill mono>
                            <span aria-hidden="true">{PHASE_OF_PLAY_META[phase].glyph}</span>
                            {PHASE_OF_PLAY_META[phase].label}
                          </GlassPill>
                        )}
                        {drill.duration_mins && <GlassPill mono>{drill.duration_mins} min</GlassPill>}
                        {isCurated && (
                          <GlassPill mono>
                            <span className="drill-card__curated-dot" />
                            Coach&apos;s Corner{drill.curator_name ? ` · ${drill.curator_name}` : ''}
                          </GlassPill>
                        )}
                        {drill.sub_themes.slice(0, 3).map((theme) => (
                          <GlassPill key={theme} mono>{theme}</GlassPill>
                        ))}
                      </div>
                      {drill.description && (
                        <p className="drill-card__desc">{drill.description}</p>
                      )}
                    </div>
                  </Card>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Pro-coach reviewers (Ben) — per-item feedback prompt on the session
          itself. Self-gates to review mode; invisible to everyone else, and
          shown regardless of calibration level. */}
      {session && id && (
        <section aria-label="Your feedback on this session" style={{ margin: '8px 0' }}>
          <ReviewFeedbackCard entityType="SESSION" entityId={id} entityName={session.title ?? 'this session'} />
        </section>
      )}

      {/* Post-session review is an academy/workbench affordance — structured
          journaling with AI-drafted narrative. Novice reflects via voice on the
          run mode mic and via the post-session Brain prompt; the structured
          form is hidden per AGENTS.md "no disabled buttons; hide unavailable
          features" + 03_wireframes.md §3 (novice variant). */}
      {session && (
        <Calibrated min="GRASSROOTS">
          <section aria-label="Post-session review">
          <Card variant="opaque" className="session-review">
            <div className="session-review__head">
              <div>
                <div className="page-section-label">Post-session review</div>
                <h2 className="session-review__title">What changed because of this session?</h2>
              </div>
              <span
                className="session-review__status"
                data-state={review?.status === 'complete' ? 'complete' : undefined}
              >
                {reviewDirty ? 'unsaved' : review?.status ?? 'not started'}
              </span>
            </div>

            <label className="session-review__field">
              <span className="session-review__label">What worked</span>
              <textarea
                className="session-review__textarea"
                value={notesWorking}
                onChange={(e) => setNotesWorking(e.target.value)}
                placeholder="Wide overloads landed once the trigger was simple."
                rows={3}
              />
            </label>

            <label className="session-review__field">
              <span className="session-review__label">What struggled</span>
              <textarea
                className="session-review__textarea"
                value={notesIssues}
                onChange={(e) => setNotesIssues(e.target.value)}
                placeholder="Decision speed dropped when pressure arrived."
                rows={3}
              />
            </label>

            <label className="session-review__field">
              <span className="session-review__label">Next-session tweak</span>
              <textarea
                className="session-review__textarea"
                value={nextTweaks}
                onChange={(e) => setNextTweaks(e.target.value)}
                placeholder="Start smaller, then add the recovering defender."
                rows={2}
              />
            </label>

            {aiNarrative !== null && (
              <div className="session-review__field">
                <div className="session-review__narrative-head">
                  <span className="session-review__label">AI narrative — edit before sharing</span>
                  <div className="session-review__narrative-actions">
                    <SpeakButton text={aiNarrative} ariaLabel="Read narrative aloud" chunked />
                    <button
                      type="button"
                      className="session-review__discard"
                      onClick={() => setAiNarrative(null)}
                      aria-label="Discard AI narrative"
                    >
                      discard
                    </button>
                  </div>
                </div>
                <textarea
                  className="session-review__textarea session-review__textarea--narrative"
                  value={aiNarrative}
                  onChange={(e) => setAiNarrative(e.target.value)}
                  rows={4}
                  placeholder="Brain's draft narrative will appear here. Edit freely before saving."
                />
              </div>
            )}

            {id && <SessionPlayerNoteShare sessionId={id} coachId={user?.id ?? null} />}

            <div className="session-review__actions">
              <Button variant="glass" size="workbench" onClick={() => void suggestTweaks()}>
                {tweaksDrafting ? 'Suggesting...' : 'Suggest tweaks'}
              </Button>
              <Button variant="glass" size="workbench" onClick={() => void draftNarrative()}>
                {narrativeDrafting ? 'Drafting narrative...' : 'Draft narrative'}
              </Button>
              <Button variant="glass" size="workbench" onClick={() => void persistReview('draft')}>
                {reviewSaving ? 'Saving...' : 'Save draft'}
              </Button>
              <Button variant="primary" size="workbench" onClick={() => void persistReview('complete')}>
                {reviewSaving ? 'Saving...' : 'Mark complete'}
              </Button>
              {reviewMessage && <span className="session-review__msg">{reviewMessage}</span>}
            </div>
          </Card>
          </section>
        </Calibrated>
      )}

      {/* Drill picker — opens from the "+ Add drill from library" button.
          Picks an existing drill + inserts a session_drills row. After a
          successful add we reload so useSessionDrills re-fetches the
          fresh list. */}
      {session && id && (
        <DrillPickerSheet
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          sessionId={id}
          sessionFit={{
            phase_coverage: session.phase_coverage,
            objectives: session.objectives,
            psych_social_intent: session.psych_social_intent,
          }}
          alreadyAddedDrillIds={new Set(drills.map((d) => d.drill?.id).filter((x): x is string => Boolean(x)))}
          onAdded={() => {
            setPickerOpen(false);
            // Soft reload to refetch the drill list. useSessionDrills doesn't
            // expose a refetch handle yet — this is the simplest path while we
            // iterate.
            window.location.reload();
          }}
        />
      )}
    </main>
  );
}
